import { Howl, Howler } from 'howler';
import { SOUND_CONFIG, DEFAULT_SOUND_CONFIG, SOUND_NAMES, type SoundName, type SoundConfig } from '@/config/soundConfig';
export { SOUND_NAMES } from '@/config/soundConfig';

export class SoundManager {
    private readonly sounds: Map<string, Howl> = new Map();
    private readonly loading: Set<string> = new Set();
    private isEnabled = true;
    private globalVolume = 1;
    private audioUnlocked = false;
    private lastClickAt = 0;
    private unlockBound = false;

    private loadSound(soundName: string, config: SoundConfig): Howl | null {
        try {
            const sound = new Howl({
                src: [`/sounds/${config.fileName}`],
                volume: (config.volume ?? 0.7) * this.globalVolume,
                loop: config.loop ?? false,
                preload: true,
            });
            this.sounds.set(soundName, sound);
            this.loading.delete(soundName);
            return sound;
        } catch {
            this.loading.delete(soundName);
            return null;
        }
    }

    // Returns cached Howl or triggers load; returns null while loading / unsupported
    private getOrLoad(soundName: string): Howl | null {
        const cached = this.sounds.get(soundName);
        if (cached) return cached;
        if (this.loading.has(soundName)) return null;
        const config = SOUND_CONFIG[soundName] ?? DEFAULT_SOUND_CONFIG;
        this.loading.add(soundName);
        return this.loadSound(soundName, config);
    }

    /** iOS / Chrome: AudioContext stays suspended until a user gesture. */
    public unlockAudio(): void {
        if (this.audioUnlocked) return;
        this.audioUnlocked = true;
        try {
            const ctx = Howler.ctx;
            if (ctx && ctx.state !== 'running') {
                void ctx.resume();
            }
        } catch {
            /* ignore */
        }
    }

    /** Resume audio on first pointer/key — call once from app bootstrap. */
    public bindUnlockOnGesture(): void {
        if (this.unlockBound || typeof window === 'undefined') return;
        this.unlockBound = true;
        const unlock = () => {
            // Create Howl first so Howler.ctx exists, then resume for iOS.
            if (this.isEnabled) this.getOrLoad(SOUND_NAMES.CLICK);
            this.unlockAudio();
        };
        window.addEventListener('pointerdown', unlock, { once: true, passive: true });
        window.addEventListener('keydown', unlock, { once: true });
    }

    public playSound(soundName: SoundName): void {
        if (!this.isEnabled) return;
        this.unlockAudio();
        try {
            const sound = this.getOrLoad(soundName);
            sound?.play();
        } catch {
            /* browser audio unavailable — never crash UI */
        }
    }

    public playClickSound(): void {
        // Dedupe Button + caller both firing on the same press.
        const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
        if (now - this.lastClickAt < 50) return;
        this.lastClickAt = now;
        this.playSound(SOUND_NAMES.CLICK);
    }

    public stopSound(soundName: SoundName): void {
        this.sounds.get(soundName)?.stop();
    }

    public stopAllSounds(): void {
        this.sounds.forEach(s => s.stop());
    }

    public setGlobalVolume(volume: number): void {
        this.globalVolume = Math.max(0, Math.min(1, volume));
        Howler.volume(this.globalVolume);
        this.sounds.forEach((sound, name) => {
            const config = SOUND_CONFIG[name] ?? DEFAULT_SOUND_CONFIG;
            sound.volume((config.volume ?? 0.7) * this.globalVolume);
        });
    }

    public getGlobalVolume(): number {
        return this.globalVolume;
    }

    public setEnabled(enabled: boolean): void {
        this.isEnabled = enabled;
        if (!enabled) this.stopAllSounds();
    }

    public isSoundEnabled(): boolean {
        return this.isEnabled;
    }

    public addSound(soundName: string, config: SoundConfig): void {
        this.loadSound(soundName, config);
    }

    public removeSound(soundName: string): void {
        const sound = this.sounds.get(soundName);
        if (sound) {
            sound.unload();
            this.sounds.delete(soundName);
        }
    }

    public getLoadedSounds(): string[] {
        return Array.from(this.sounds.keys());
    }

    public isSoundLoaded(soundName: string): boolean {
        return this.sounds.has(soundName);
    }
}

const soundManager = new SoundManager();
if (typeof window !== 'undefined') {
    soundManager.bindUnlockOnGesture();
}

export const playSound = (soundName: SoundName): void => soundManager.playSound(soundName);
export const playClickSound = (): void => soundManager.playClickSound();
export const unlockAudio = (): void => soundManager.unlockAudio();
export const stopSound = (soundName: SoundName): void => soundManager.stopSound(soundName);
export const stopAllSounds = (): void => soundManager.stopAllSounds();
export const setGlobalVolume = (volume: number): void => soundManager.setGlobalVolume(volume);
export const getGlobalVolume = (): number => soundManager.getGlobalVolume();
export const setSoundEnabled = (enabled: boolean): void => soundManager.setEnabled(enabled);
export const isSoundEnabled = (): boolean => soundManager.isSoundEnabled();
export { soundManager };
